/**
 * engineNormalJson_Merge_TemplateTraversal.ts - Main template traversal for EngineNormalJson
 * Pure ES Module version
 *
 * Uses new HtmlJson format with type prefixes:
 * s=Html, k=JsonKey, K=JsonKeySkip, p=Placeholder, P=PlaceholderSkip,
 * h=Slotholder, H=SlotholderSkip, a=ArrayBlock, A=ArrayBlockSkip, x=SlotHtml, i=Slot, c=Conditional
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { getKeysList } from './engineNormalJson_Helper.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { tryReturnStaticHtml } from './engineNormalJson_Merge_Metadata.js';

// Import constants
import {
    TYPE_HTML,
    TYPE_JSONKEY,
    TYPE_JSONKEY_SKIP,
    TYPE_PLACEHOLDER,
    TYPE_PLACEHOLDER_SKIP,
    TYPE_SLOTHOLDER,
    TYPE_SLOTHOLDER_SKIP,
    TYPE_ARRAYBLOCK,
    TYPE_ARRAYBLOCK_SKIP,
    TYPE_SLOTHTML,
    TYPE_SLOT,
    TYPE_CONDITIONAL,
    META_KEY,
    isTypePrefixedKey
} from './engineNormalJson_Merge_Constants.js';

// Import processing modules
import { ProcessJsonKey } from './engineNormalJson_Merge_JsonKey.js';
import { processArrayBlockSkip } from './engineNormalJson_Merge_ArrayBlock.js';
import { mergeSlotComponent } from './engineNormalJson_Merge_Slot.js';

import type { EngineNormalJson } from './engineNormalJson.js';
import type { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';

export const templateTraversalMethods = {
    mergeTemplate(
        this: EngineNormalJson,
        htmlJson: JsonObject,
        appSite: string,
        currentTemplateName: string,
        appView: string | null,
        templateKey: string,
        operations: TemplateOperation[],
        jsonData: JsonObject,
        componentHtml: JsonObject | null,
        flowStack: string[] | null = null,
        genComponentHtml: boolean,
        contextJson: JsonObject | null = null,
        configJson: JsonObject | null = null,
        recursionDepth: number = 0,
        parentComponentSink: TemplateOperation[] | null = null,
        keyReplaceStage: TemplateOperation[] | null = null,
        keyMissingStage: TemplateOperation[] | null = null,
        parentSlotSink: TemplateOperation[] | null = null,
        clientMode: boolean = false
    ): string {
        const maxRecursionDepth = this._loader!.maxRecursionDepth;
        if (recursionDepth > maxRecursionDepth) {
            const errorMsg = `[ERROR] Maximum recursion depth (${maxRecursionDepth}) exceeded for template ${templateKey}. Possible circular template reference.`;
            Logger.error(errorMsg, 'EngineNormalJson');
            return errorMsg;
        }

        const staticResult = tryReturnStaticHtml(htmlJson);
        if (staticResult.success) {
            Logger.flow(`MergeTemplate: staticHtml templateKey=${templateKey} depth=${recursionDepth} size=${staticResult.staticHtml.length}`, "NormalJson.TemplateRecursion");
            return staticResult.staticHtml;
        }

        Logger.flow(`MergeTemplate: enter templateKey=${templateKey} depth=${recursionDepth} keys=${htmlJson.size}`, "NormalJson.TemplateRecursion");
        let result = '';
        const keys = getKeysList([...htmlJson.keys()]);

        // Multi-stage ops emission mirroring Normal HTML engine's MergeTemplateWithJson phases:
        // ArraySkip → ArrayIteration → KeySkip → KeyReplace → KeyMissing → ConditionalBlock → Slots → Components
        // KeyReplace+KeyMissing come from MergeDataJsonIntoHtmlJson via keyReplaceStage/keyMissingStage.
        // Normal runs MergeTemplateSlots (all slotholders) then ReplaceTemplatePlaceholders (all
        // components), so slot ops always precede component ops regardless of document order.
        // Slot/Component ops route to parentSlotSink/parentComponentSink when set so that
        // conditional-body slots/components hoist to the OUTER template's slot/component phase.
        const arraySkipOps: TemplateOperation[] = [];
        const arrayIterOps: TemplateOperation[] = [];
        const keySkipOps: TemplateOperation[] = [];
        const conditionalOps: TemplateOperation[] = [];
        const ownsComponentSink = parentComponentSink === null;
        const componentPhaseOps: TemplateOperation[] = parentComponentSink ?? [];
        const ownsSlotSink = parentSlotSink === null;
        const slotPhaseOps: TemplateOperation[] = parentSlotSink ?? [];

        for (const key of keys) {
            if (key === META_KEY) {
                continue;
            }

            const value = htmlJson.get(key);
            const keyType = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';
            const keyName = GenerateHtmlJson.getKeyName(key);

            if (keyType === TYPE_HTML || keyType === TYPE_SLOTHTML) {
                result += value.asString() ?? '';
            } else if (keyType === TYPE_PLACEHOLDER || keyType === TYPE_PLACEHOLDER_SKIP) {
                const params = {
                    key,
                    keyType,
                    keyName,
                    value,
                    appSite,
                    currentTemplateName,
                    appView,
                    templateKey,
                    operations: componentPhaseOps,
                    jsonData,
                    componentHtml,
                    genComponentHtml,
                    recursionDepth,
                    contextJson,
                    flowStack,
                    configJson,
                    clientMode
                };
                const { result: placeholderResult, continue: shouldContinue } = this.ProcessPlaceholder(params);
                result += placeholderResult;
                if (shouldContinue) {
                    continue;
                }
            } else if (keyType === TYPE_SLOTHOLDER || keyType === TYPE_SLOTHOLDER_SKIP) {
                const slotObj = value.asObject();
                const params = {
                    key,
                    keyType,
                    keyName,
                    value: slotObj ?? new JsonObject(),
                    appSite,
                    currentTemplateName,
                    appView,
                    templateKey,
                    operations: slotPhaseOps,
                    jsonData,
                    componentHtml,
                    genComponentHtml,
                    recursionDepth,
                    contextJson,
                    flowStack,
                    configJson,
                    clientMode
                };
                const { result: slotholderResult } = this.ProcessSlotholder(params);
                result += slotholderResult;
            } else if (keyType === TYPE_JSONKEY) {
                // Non-skip JsonKey emits no op here (KeyReplace/KeyMissing come from
                // MergeDataJsonIntoHtmlJson into keyReplaceStage/keyMissingStage).
                const keyResult: string[] = [];
                ProcessJsonKey(this, { append: (value: string) => keyResult.push(value) }, key, value, false, templateKey, currentTemplateName, operations, jsonData);
                result += keyResult.join('');
            } else if (keyType === TYPE_JSONKEY_SKIP) {
                // KeySkip op routes to keySkipOps (phase 3).
                const keyResult: string[] = [];
                ProcessJsonKey(this, { append: (value: string) => keyResult.push(value) }, key, value, true, templateKey, currentTemplateName, keySkipOps, jsonData);
                result += keyResult.join('');
            } else if (keyType === TYPE_SLOT) {
                result += value.asString() ?? '';
            } else if (keyType === TYPE_ARRAYBLOCK) {
                const arrayObj = value.asObject();
                const params = {
                    keyName,
                    value: arrayObj ?? new JsonObject(),
                    appSite,
                    currentTemplateName,
                    appView,
                    templateKey,
                    operations: arrayIterOps,
                    jsonData,
                    componentHtml,
                    genComponentHtml,
                    recursionDepth,
                    contextJson,
                    flowStack,
                    configJson,
                    clientMode
                };
                result += this.processArrayBlockEntry(params);
            } else if (keyType === TYPE_ARRAYBLOCK_SKIP) {
                const params = {
                    keyName,
                    currentTemplateName,
                    templateKey,
                    operations: arraySkipOps
                };
                result += processArrayBlockSkip(this, params);
            } else if (keyType === TYPE_CONDITIONAL) {
                const condObj = value.asObject();
                const params = {
                    keyName,
                    value: condObj ?? new JsonObject(),
                    appSite,
                    currentTemplateName,
                    appView,
                    templateKey,
                    operations: conditionalOps,
                    jsonData,
                    componentHtml,
                    genComponentHtml,
                    recursionDepth,
                    contextJson,
                    flowStack,
                    configJson,
                    parentComponentSink: componentPhaseOps,
                    parentSlotSink: slotPhaseOps,
                    clientMode
                };
                result += this.processConditionalBlock(params);
            } else {
                const nestedObj = value.asObject();
                if (nestedObj) {
                    // Hoist nested-object slot/component ops to this level's phase lists. The
                    // keyReplace/keyMissing stages are pre-merged and flushed at THIS level only,
                    // so pass null to avoid double-flushing them in the nested call.
                    const nested = this.mergeTemplate(nestedObj, appSite, currentTemplateName, appView, templateKey, operations, jsonData, componentHtml, flowStack, genComponentHtml, contextJson, configJson, recursionDepth + 1, componentPhaseOps, null, null, slotPhaseOps, clientMode);
                    result += nested;
                }
            }
        }

        // Canonical emission order per Normal HTML engine:
        // ArraySkip → ArrayIteration → KeySkip → KeyReplace → KeyMissing → ConditionalBlock → Slots → Components
        operations.push(...arraySkipOps);
        operations.push(...orderArrayIterOpsByDataKey(arrayIterOps, jsonData));
        operations.push(...keySkipOps);
        if (keyReplaceStage) operations.push(...keyReplaceStage);
        if (keyMissingStage) operations.push(...keyMissingStage);
        operations.push(...conditionalOps);
        // Slots flush before components, mirroring Normal's MergeTemplateSlots-then-ReplaceTemplatePlaceholders.
        // Only flush local slot/component ops when we own the sink (caller-provided sinks were
        // already routed there directly).
        if (ownsSlotSink) operations.push(...slotPhaseOps);
        if (ownsComponentSink) operations.push(...componentPhaseOps);

        Logger.flow(`MergeTemplate: exit templateKey=${templateKey} depth=${recursionDepth} size=${result.length}`, "NormalJson.TemplateRecursion");
        return result;
    },

    mergeSlotComponent(
        this: EngineNormalJson,
        slotHtmlJson: JsonObject,
        slotholderData: JsonObject,
        appSite: string,
        slotName: string,
        appView: string | null,
        slotKey: string,
        operations: TemplateOperation[],
        jsonData: JsonObject,
        slotContentSourceTemplate: string,
        componentHtml: JsonObject | null,
        flowStack: string[] | null,
        genComponentHtml: boolean,
        contextJson: JsonObject | null,
        configJson: JsonObject | null = null,
        recursionDepth: number,
        clientMode: boolean = false
    ): string {
        return mergeSlotComponent(this, slotHtmlJson, slotholderData, appSite, slotName, appView, slotKey, operations, jsonData, slotContentSourceTemplate, componentHtml, flowStack, genComponentHtml, contextJson, configJson, recursionDepth, clientMode);
    }
};

function orderArrayIterOpsByDataKey(arrayIterOps: TemplateOperation[], jsonData: JsonObject): TemplateOperation[] {
    if (arrayIterOps.length <= 1 || !jsonData || jsonData.size === 0) {
        return arrayIterOps;
    }

    const keyIndex = new Map<string, number>();
    let dataIndex = 0;
    for (const key of jsonData.keys()) {
        const lower = key.toLowerCase();
        if (!keyIndex.has(lower)) {
            keyIndex.set(lower, dataIndex);
        }
        dataIndex++;
    }

    return arrayIterOps
        .map((op, originalIndex) => {
            const arrayKey = extractArrayKeyName(op.target).toLowerCase();
            return {
                op,
                originalIndex,
                dataIndex: arrayKey && keyIndex.has(arrayKey) ? keyIndex.get(arrayKey)! : Number.MAX_SAFE_INTEGER
            };
        })
        .sort((a, b) => a.dataIndex !== b.dataIndex ? a.dataIndex - b.dataIndex : a.originalIndex - b.originalIndex)
        .map(item => item.op);
}

function extractArrayKeyName(target: string | null | undefined): string {
    if (!target) {
        return '';
    }
    let s = target.trim();
    if (s.startsWith('{{')) {
        s = s.substring(2);
    }
    if (s.endsWith('}}')) {
        s = s.substring(0, s.length - 2);
    }
    return s.replace(/^[!@#^/]+/, '').trim();
}

export const { mergeTemplate } = templateTraversalMethods;
